import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, Phone, Calendar, Clock, ShieldCheck, Sparkles, MapPin, MessageSquareQuote, Mic, Heart, Compass, RefreshCw } from "lucide-react";
import ContactSection from "../components/ContactSection";
import FaqSection from "../components/FaqSection";

export default function Clairvoyance() {
  return (
    <>
      <Helmet>
        <title>Clairvoyance med en erfaren clairvoyant i Aarhus | John Calmar</title>
        <meta
          name="description"
          content="Clairvoyance med en erfaren clairvoyant i Aarhus. Få en personlig samtale med plads til dine spørgsmål, din tvivl og de valg, du står overfor hos John Calmar."
        />
        <link rel="canonical" href="https://johncalmar.dk/clairvoyance-online" />
        <meta property="og:title" content="Clairvoyance med en erfaren clairvoyant i Aarhus | John Calmar" />
        <meta
          property="og:description"
          content="Clairvoyance med en erfaren clairvoyant i Aarhus. Få en personlig samtale med plads til dine spørgsmål, din tvivl og de valg, du står overfor hos John Calmar."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmars-univers-klarsyn.jpg"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Clairvoyance med en erfaren clairvoyant i Aarhus | John Calmar" />
        <meta
          name="twitter:description"
          content="Clairvoyance med en erfaren clairvoyant i Aarhus. Få en personlig samtale med plads til dine spørgsmål, din tvivl og de valg, du står overfor hos John Calmar."
        />
        <meta
          name="twitter:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmars-univers-klarsyn.jpg"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-brand-50 py-20 md:py-28 overflow-hidden border-b border-brand-200/40">
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Hero Content */}
          <div className="lg:col-span-7 text-left space-y-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-brand-200/60 text-brand-700 text-xs tracking-wider uppercase font-medium">
              <Sparkles className="w-3.5 h-3.5 text-brand-600" />
              <span>Personlig samtale & klarsyn</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-light text-neutral-950 leading-[1.2] tracking-tight">
              Clairvoyance med en erfaren clairvoyant
              <span className="font-display italic text-brand-600 block mt-1 font-normal">
                – i Aarhus
              </span>
            </h1>

            <div className="space-y-3.5 text-neutral-700 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
              <p className="text-lg sm:text-xl text-neutral-800 font-normal">
                Når noget fylder, kan clairvoyance give dig et nyt blik på din situation.
              </p>
              <p>
                Jeg hedder{" "}
                <Link
                  to="/"
                  className="text-neutral-900 font-medium hover:text-brand-600 underline underline-offset-4 decoration-brand-400/60 hover:decoration-brand-600 transition-colors"
                >
                  John Calmar
                </Link>{" "}
                og har mere end 40 års erfaring som clairvoyant. Gennem professionel clairvoyance hjælper jeg dig med at skabe overblik, klarhed og fornyet ro.
              </p>
              <p>
                Hos mig får du en personlig samtale med clairvoyance, hvor der er fuld plads til dine spørgsmål, din tvivl og de store eller små valg, du står overfor.
              </p>
              <p className="text-neutral-600 text-sm sm:text-base pt-1 border-l-2 border-brand-200 pl-3.5">
                Udover clairvoyant rådgivning og clairvoyance tilbyder jeg også{" "}
                <Link
                  to="/hypnose"
                  className="text-neutral-900 font-medium hover:text-brand-600 underline underline-offset-4 decoration-brand-400/60 hover:decoration-brand-600 transition-colors"
                >
                  hypnose
                </Link>
                , hvis du ønsker at arbejde målrettet med vaner, frygt eller fastlåste mønstre.
              </p>
            </div>

            {/* USPs directly in the Hero Area */}
            <div className="space-y-3 max-w-xl pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 text-sm text-neutral-700 font-medium">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Mere end 40 års erfaring</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-neutral-700 font-medium">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Møde i Viby J ved Aarhus</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-neutral-700 font-medium">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Online clairvoyance & telefon</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-neutral-700 font-medium">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Jordnært sprog & ærlig formidling</span>
                </div>
              </div>
            </div>

            {/* Visually Strong CTA */}
            <div className="pt-2 flex flex-col sm:flex-row gap-4">
              <a
                href="#kontakt"
                className="px-8 py-4 bg-brand-600 hover:bg-brand-700 text-white hover:text-white font-semibold rounded-xl text-center flex items-center justify-center gap-2 transition-all duration-200 shadow-lg shadow-brand-600/15"
              >
                <Calendar className="w-4 h-4" />
                Bestil tid til clairvoyance
              </a>
              <a
                href="tel:40938593"
                className="px-8 py-4 bg-white hover:bg-neutral-50 border border-neutral-200 hover:border-neutral-300 text-neutral-800 font-semibold rounded-xl text-center flex items-center justify-center gap-2 transition-all duration-200"
              >
                <Phone className="w-4 h-4" />
                +45 40 93 85 93
              </a>
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            <div className="relative w-full aspect-[4/5] overflow-hidden border border-brand-200/60 shadow-2xl rounded-2xl">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-clairvoyant-aarhus-session.jpg"
                alt="John Calmar – Erfaren clairvoyant i Viby J ved Aarhus til personlig rådgivning og klarsyn"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm p-4 rounded-xl border border-neutral-200 text-left shadow-md">
                <p className="font-display font-medium text-neutral-900 text-sm">John Calmar</p>
                <p className="text-xs text-neutral-600">Clairvoyant, hypnoterapeut & underviser i over 40 år</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main SEO Content Section with Sticky Sidebar (Price Box & Contact) */}
      <section className="bg-white py-16 md:py-24 border-y border-neutral-100">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Primary Article Content (7 cols on desktop) */}
            <div className="lg:col-span-7 space-y-14 text-left">

              {/* Section 1: Hvad er clairvoyance, og hvordan opleves klarsyn? */}
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 border border-brand-200/60 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 text-brand-600" />
                  <span>Indsigt & intuition</span>
                </div>
                
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                  Hvad er clairvoyance, og hvordan opleves klarsyn?
                </h2>

                {/* Lead highlight card with sensory channels */}
                <div className="relative bg-gradient-to-br from-brand-50/70 via-white to-brand-50/40 border-l-4 border-brand-600 border-y border-r border-neutral-200/70 rounded-2xl p-6 sm:p-7 space-y-4 shadow-sm">
                  <p className="text-neutral-800 text-lg sm:text-xl font-display font-light leading-relaxed">
                    Hvad er clairvoyance? I min tilgang er det en spirituel måde at modtage indtryk om mennesker, relationer og temaer i dit liv. De kan komme som billeder, ord, følelser eller intuition. Jeg formidler det, jeg oplever, i et enkelt og jordnært sprog.
                  </p>

                  {/* Creative sensory pills */}
                  <div className="pt-2 border-t border-brand-200/50">
                    <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold block mb-2.5">
                      Indtrykkene kan opleves som:
                    </span>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-white/90 border border-brand-200/70 rounded-xl px-3 py-2 text-center shadow-xs">
                        <span className="text-xs font-medium text-neutral-800 flex items-center justify-center gap-1.5">
                          <Sparkles className="w-3 h-3 text-brand-600" />
                          Billeder
                        </span>
                      </div>
                      <div className="bg-white/90 border border-brand-200/70 rounded-xl px-3 py-2 text-center shadow-xs">
                        <span className="text-xs font-medium text-neutral-800 flex items-center justify-center gap-1.5">
                          <MessageSquareQuote className="w-3 h-3 text-brand-600" />
                          Ord
                        </span>
                      </div>
                      <div className="bg-white/90 border border-brand-200/70 rounded-xl px-3 py-2 text-center shadow-xs">
                        <span className="text-xs font-medium text-neutral-800 flex items-center justify-center gap-1.5">
                          <Heart className="w-3 h-3 text-brand-600" />
                          Følelser
                        </span>
                      </div>
                      <div className="bg-white/90 border border-brand-200/70 rounded-xl px-3 py-2 text-center shadow-xs">
                        <span className="text-xs font-medium text-neutral-800 flex items-center justify-center gap-1.5">
                          <Compass className="w-3 h-3 text-brand-600" />
                          Intuition
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 text-neutral-600 font-light text-base sm:text-lg leading-relaxed">
                  <p>
                    Clairvoyance og klarsyn bruges ofte om det samme. Det handler ikke alene om fremtiden, men kan også sætte lys på fortiden og din situation lige nu. Samtalen giver dig nye perspektiver, som du kan holde op mod dine egne erfaringer og bruge i dine valg.
                  </p>
                </div>

                {/* Visual timeline / perspective bar */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-1">
                  <div className="bg-neutral-50 border border-neutral-200/70 rounded-xl p-3 text-center">
                    <span className="text-xs font-semibold text-brand-700 block">Fortiden</span>
                    <span className="text-[11px] text-neutral-500 font-light leading-tight block mt-0.5">Erfaringer & baggrund</span>
                  </div>
                  <div className="bg-neutral-50 border border-neutral-200/70 rounded-xl p-3 text-center">
                    <span className="text-xs font-semibold text-brand-700 block">Lige nu</span>
                    <span className="text-[11px] text-neutral-500 font-light leading-tight block mt-0.5">Din nuværende situation</span>
                  </div>
                  <div className="bg-neutral-50 border border-neutral-200/70 rounded-xl p-3 text-center">
                    <span className="text-xs font-semibold text-brand-700 block">Fremtiden</span>
                    <span className="text-[11px] text-neutral-500 font-light leading-tight block mt-0.5">Perspektiver & nye valg</span>
                  </div>
                </div>
              </div>

              {/* Section 2: Clairvoyance og klarsyn giver perspektiv, ikke et fast facit */}
              <div className="space-y-5 pt-8 border-t border-neutral-100">
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                  Clairvoyance og klarsyn giver perspektiv, ikke et fast facit
                </h2>
                
                <div className="bg-stone-50/80 border border-stone-200/80 rounded-2xl p-6 sm:p-7 space-y-4">
                  <div className="flex items-start gap-3.5">
                    <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0 mt-0.5">
                      <Compass className="w-5 h-5" />
                    </div>
                    <div className="space-y-3">
                      <p className="text-neutral-800 text-base sm:text-lg font-light leading-relaxed">
                        Fremtiden er ikke et fast facit. Når jeg arbejder med clairvoyance og klarsyn, fortæller jeg ærligt, hvad jeg oplever, men jeg træffer ikke dine beslutninger. Formålet med en clairvoyance er at give dig inspiration og klarhed, så du selv kan vælge det, der føles rigtigt, og lade de dele ligge, som ikke giver mening for dig.
                      </p>
                      <p className="text-neutral-600 text-base font-light leading-relaxed">
                        Under en clairvoyance kan der komme indtryk om åndelige guider, afdøde, aura eller tidligere liv. Det er elementer i min spirituelle tilgang og ikke noget, jeg kan love på forhånd. Ønsker du en målrettet analyse af din krops energifelter og auraens farver, tilbyder jeg også specialiseret <Link to="/auratydning" className="text-brand-700 hover:text-brand-800 underline underline-offset-4 font-normal">auratydning med aurakamera</Link>, ligesom min visdom bygger på mange års praksis med <Link to="/shamanisme" className="text-brand-700 hover:text-brand-800 underline underline-offset-4 font-normal">shamanisme og naturkræfter</Link>.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-200/60">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-xs font-medium text-neutral-700 border border-neutral-200 shadow-xs">
                      <Check className="w-3.5 h-3.5 text-brand-600" />
                      Ingen faste facitter
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-xs font-medium text-neutral-700 border border-neutral-200 shadow-xs">
                      <Check className="w-3.5 h-3.5 text-brand-600" />
                      Dine egne valg gælder altid
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-xs font-medium text-neutral-700 border border-neutral-200 shadow-xs">
                      <Check className="w-3.5 h-3.5 text-brand-600" />
                      Fri for dogmer og pres
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 3: Hvornår kan clairvoyant rådgivning være det rette valg? */}
              <div className="space-y-6 pt-8 border-t border-neutral-100">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                    <Heart className="w-3.5 h-3.5 text-brand-600" />
                    <span>Livstemaer & vejledning</span>
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                    Hvornår kan clairvoyance og rådgivning være det rette valg?
                  </h2>
                </div>

                <p className="text-neutral-600 font-light text-base sm:text-lg leading-relaxed">
                  Clairvoyance og clairvoyant rådgivning kan være relevant, når tankerne kører i ring, du savner retning eller står ved en vigtig korsvej i livet. Du kan komme til en clairvoyance med et bestemt spørgsmål eller en mere åben situation. Vi tager udgangspunkt i det, som fylder for dig her og nu.
                </p>

                {/* Creative 2x2 Grid for the four main themes */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs hover:shadow-sm space-y-2.5">
                    <div className="w-9 h-9 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center">
                      <Heart className="w-4 h-4" />
                    </div>
                    <h3 className="font-display font-medium text-neutral-900 text-lg">
                      Kærlighed & relationer
                    </h3>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed">
                      Kærlighed, familie, venskaber og andre nære relationer
                    </p>
                  </div>

                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs hover:shadow-sm space-y-2.5">
                    <div className="w-9 h-9 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center">
                      <Compass className="w-4 h-4" />
                    </div>
                    <h3 className="font-display font-medium text-neutral-900 text-lg">
                      Arbejde & retning
                    </h3>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed">
                      Arbejde, livsretning, grænser og personlige valg
                    </p>
                  </div>

                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs hover:shadow-sm space-y-2.5">
                    <div className="w-9 h-9 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center">
                      <RefreshCw className="w-4 h-4" />
                    </div>
                    <h3 className="font-display font-medium text-neutral-900 text-lg">
                      Mønstre & forandring
                    </h3>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed">
                      Gamle mønstre, tilbagevendende tvivl og ønsker om forandring
                    </p>
                  </div>

                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs hover:shadow-sm space-y-2.5">
                    <div className="w-9 h-9 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <h3 className="font-display font-medium text-neutral-900 text-lg">
                      Den åndelige dimension
                    </h3>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed">
                      Åndelige guider, afdøde, aura og tidligere liv
                    </p>
                  </div>
                </div>

                <div className="p-4 sm:p-5 rounded-xl bg-neutral-50 border border-neutral-200/70 text-neutral-600 font-light text-base sm:text-lg leading-relaxed">
                  <p>
                    Du bestemmer, hvad vi skal fokusere på. Under en clairvoyance kan der også dukke andre vinkler op end dem, du havde tænkt på hjemmefra. Jeg formidler indtrykkene respektfuldt, så du får et bredere grundlag for at forstå din situation og vælge dit næste skridt.
                  </p>
                </div>
              </div>

              {/* Section 4: Sådan får du mest ud af din clairvoyance */}
              <div className="bg-gradient-to-br from-brand-50/90 via-white to-brand-100/40 border border-brand-300/70 rounded-2xl p-6 sm:p-8 shadow-sm space-y-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-brand-500/10 rounded-full blur-2xl pointer-events-none" />
                
                <div className="flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 text-brand-600" />
                  <span>God forberedelse</span>
                </div>

                <h2 className="font-display text-2xl sm:text-3xl font-light text-neutral-950 leading-tight">
                  Sådan får du mest ud af din clairvoyance
                </h2>

                <p className="text-neutral-700 text-base sm:text-lg font-light leading-relaxed">
                  Du behøver hverken særlige forkundskaber eller en fast spirituel overbevisning. Skriv gerne dine vigtigste spørgsmål ned inden mødet, så du husker dem. Kom med et åbent sind, men behold din sunde dømmekraft og del ikke mere, end du er tryg ved.
                </p>

                {/* 3 scannable takeaways */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                  <div className="bg-white/80 border border-brand-200/60 rounded-xl p-3.5">
                    <span className="text-xs font-semibold text-neutral-900 block">1. Skriv spørgsmål ned</span>
                    <span className="text-xs text-neutral-500 font-light mt-0.5 block">Så du husker det vigtigste</span>
                  </div>
                  <div className="bg-white/80 border border-brand-200/60 rounded-xl p-3.5">
                    <span className="text-xs font-semibold text-neutral-900 block">2. Åbent sind</span>
                    <span className="text-xs text-neutral-500 font-light mt-0.5 block">Klar til nye vinkler</span>
                  </div>
                  <div className="bg-white/80 border border-brand-200/60 rounded-xl p-3.5">
                    <span className="text-xs font-semibold text-neutral-900 block">3. Sund dømmekraft</span>
                    <span className="text-xs text-neutral-500 font-light mt-0.5 block">Del kun hvad du er tryg ved</span>
                  </div>
                </div>
              </div>

              {/* Section 5: Sådan foregår en session hos en clairvoyant (Engaging Step-by-Step Flow) */}
              <div className="space-y-6 pt-8 border-t border-neutral-100">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                    <Clock className="w-3.5 h-3.5 text-brand-600" />
                    <span>Forløb & rammer</span>
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                    Sådan foregår en session med clairvoyance
                  </h2>
                </div>

                {/* 3 Step Visual Flow */}
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4 p-5 sm:p-6 bg-white border border-neutral-200/80 rounded-2xl shadow-xs">
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-display font-medium text-neutral-900 text-lg">
                        Tidsbestilling & indledende samtale om clairvoyance
                      </h3>
                      <p className="text-neutral-600 font-light text-base leading-relaxed">
                        Når du ønsker en tid til clairvoyance, sender du dine kontaktoplysninger og fortæller, hvilken session du er interesseret i. Jeg kontakter dig, så vi kan aftale tidspunkt og form. Ved begyndelsen af din clairvoyance taler vi kort om dine ønsker, før jeg sætter ord på de indtryk, jeg modtager.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4 p-5 sm:p-6 bg-white border border-neutral-200/80 rounded-2xl shadow-xs">
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </div>
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-display font-medium text-neutral-900 text-lg">
                          Plads til spørgsmål under din clairvoyance & lydoptagelse
                        </h3>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium border border-brand-200">
                          <Mic className="w-3 h-3 text-brand-600" />
                          Optagelse tilladt
                        </span>
                      </div>
                      <p className="text-neutral-600 font-light text-base leading-relaxed">
                        Der er god plads til dine spørgsmål undervejs i samtalen. Du må gerne optage din clairvoyance på din telefon, så du kan høre den igen i ro. Det kan være en fordel, fordi en clairvoyance kan rumme mange pointer, som først bliver helt tydelige, når du genhører dem efterfølgende.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4 p-5 sm:p-6 bg-white border border-neutral-200/80 rounded-2xl shadow-xs">
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </div>
                    <div className="space-y-3">
                      <h3 className="font-display font-medium text-neutral-900 text-lg">
                        Valg af varighed og form for clairvoyance
                      </h3>
                      <p className="text-neutral-600 font-light text-base leading-relaxed">
                        Du kan vælge den varighed, der passer til dit behov og antallet af spørgsmål. Priserne gælder både clairvoyance ved fremmøde i klinikken samt clairvoyance online eller over telefonen. Er du i tvivl om valget af clairvoyance, afklarer vi det blot sammen inden din booking.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Creative Price Cards Grid */}
                <div className="pt-2">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                    <div className="relative group bg-white border border-brand-200/80 hover:border-brand-400 rounded-2xl p-5 text-center transition-all shadow-xs hover:shadow-md">
                      <div className="text-xs uppercase tracking-wider text-brand-700 font-semibold mb-1">Kort session</div>
                      <div className="text-3xl font-display font-light text-neutral-900">350 kr.</div>
                      <p className="text-xs text-neutral-600 font-medium mt-2 pt-2 border-t border-neutral-100">
                        15 minutters clairvoyance koster 350 kr.
                      </p>
                    </div>

                    <div className="relative group bg-white border border-brand-200/80 hover:border-brand-400 rounded-2xl p-5 text-center transition-all shadow-xs hover:shadow-md">
                      <div className="text-xs uppercase tracking-wider text-brand-700 font-semibold mb-1">Mest valgte</div>
                      <div className="text-3xl font-display font-light text-neutral-900">650 kr.</div>
                      <p className="text-xs text-neutral-600 font-medium mt-2 pt-2 border-t border-neutral-100">
                        30 minutters clairvoyance koster 650 kr.
                      </p>
                    </div>

                    <div className="relative group bg-white border border-brand-200/80 hover:border-brand-400 rounded-2xl p-5 text-center transition-all shadow-xs hover:shadow-md">
                      <div className="text-xs uppercase tracking-wider text-brand-700 font-semibold mb-1">Dybdegående</div>
                      <div className="text-3xl font-display font-light text-neutral-900">1.200 kr.</div>
                      <p className="text-xs text-neutral-600 font-medium mt-2 pt-2 border-t border-neutral-100">
                        60 minutters clairvoyance koster 1.200 kr.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 6: Hvad kan du forvente af clairvoyance? (Ethical Disclaimer Card) */}
              <div className="p-6 sm:p-8 rounded-2xl bg-stone-50 border border-stone-200/90 space-y-4 shadow-xs">
                <div className="flex items-center gap-2 text-neutral-800 text-xs font-semibold uppercase tracking-wider">
                  <ShieldCheck className="w-4 h-4 text-brand-600" />
                  <span>Etik & forventningsafstemning</span>
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-light text-neutral-950 leading-tight">
                  Hvad kan du forvente af clairvoyance?
                </h2>
                <p className="text-neutral-600 text-base sm:text-lg font-light leading-relaxed">
                  Jeg kan ikke garantere et bestemt budskab, en præcis forudsigelse eller kontakt med en bestemt afdød. Clairvoyant rådgivning er spirituel vejledning og erstatter ikke lægelig, psykologisk, juridisk eller økonomisk rådgivning. Når en situation kræver faglig hjælp, bør clairvoyance bruges som et muligt supplement og ikke som behandling.
                </p>
              </div>

              {/* Section 7: Clairvoyance i Aarhus, online eller over telefonen */}
              <div className="space-y-6 pt-8 border-t border-neutral-100">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                    <MapPin className="w-3.5 h-3.5 text-brand-600" />
                    <span>Fleksible rammer</span>
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                    Clairvoyance i Aarhus, online eller over telefonen
                  </h2>
                </div>

                {/* Creative Side-by-Side Location Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white border border-brand-200/80 rounded-2xl p-5 sm:p-6 space-y-3 shadow-xs">
                    <div className="w-9 h-9 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <h3 className="font-display font-medium text-neutral-900 text-lg">
                      I klinikken ved Aarhus
                    </h3>
                    <p className="text-neutral-600 text-sm sm:text-base font-light leading-relaxed">
                      Du kan møde mig på Lykkesholms Allé 102, 1. th., 8260 Viby J ved Aarhus. Her foregår clairvoyance i rolige rammer med tid til spørgsmål og eftertanke. Søger du clairvoyance i Aarhus eller en clairvoyant i Aarhus, er min praksis en lokal mulighed tæt på byen.
                    </p>
                  </div>

                  <div className="bg-white border border-brand-200/80 rounded-2xl p-5 sm:p-6 space-y-3 shadow-xs">
                    <div className="w-9 h-9 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center">
                      <Phone className="w-4 h-4" />
                    </div>
                    <h3 className="font-display font-medium text-neutral-900 text-lg">
                      Online & telefonisk
                    </h3>
                    <p className="text-neutral-600 text-sm sm:text-base font-light leading-relaxed">
                      Hvis du foretrækker at deltage hjemmefra, tilbyder jeg online clairvoyance og telefonisk clairvoyance. Vi arbejder med de samme spørgsmål og det samme rolige fokus som ved et personligt møde. Det gør rådgivningen tilgængelig for dig, uanset hvor i Danmark du bor. Ønsker du at deltage i ugentlige klarsyn og meditationer i et trygt fællesskab, kan du også læse om mit online univers <Link to="/calmars-univers" className="text-brand-700 hover:text-brand-800 underline underline-offset-4 font-normal">Calmars Univers</Link> samt vores faste <Link to="/klarsynsaften-online" className="text-brand-700 hover:text-brand-800 underline underline-offset-4 font-normal">klarsynsaftener online</Link>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 8: Er personlig clairvoyance dit næste skridt? */}
              <div className="relative p-7 sm:p-9 rounded-3xl bg-gradient-to-br from-brand-50 via-white to-brand-100/50 border border-brand-300/80 space-y-5 shadow-sm overflow-hidden">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-brand-400/10 rounded-full blur-2xl pointer-events-none" />
                
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                  Er personlig clairvoyance dit næste skridt?
                </h2>
                <p className="text-neutral-700 text-base sm:text-lg font-light leading-relaxed">
                  Hvis du søger indsigt, ro eller nye perspektiver, er en personlig clairvoyance en oplagt mulighed. Som din clairvoyant møder jeg dig åbent og respektfuldt uden færdige facitter på dit liv. Målet med din clairvoyance er, at du får jordnære budskaber og reel klarhed over dine næste skridt.
                </p>
                <div className="pt-2 flex flex-col sm:flex-row gap-3.5">
                  <a
                    href="#kontakt"
                    className="px-7 py-3.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl text-center flex items-center justify-center gap-2 text-sm transition-all shadow-md shadow-brand-600/20"
                  >
                    <Calendar className="w-4 h-4" />
                    Bestil en tid nu
                  </a>
                  <a
                    href="tel:40938593"
                    className="px-7 py-3.5 bg-white hover:bg-neutral-50 border border-brand-200 hover:border-brand-300 text-neutral-800 font-semibold rounded-xl text-center flex items-center justify-center gap-2 text-sm transition-colors shadow-xs"
                  >
                    <Phone className="w-4 h-4" />
                    +45 40 93 85 93
                  </a>
                </div>
              </div>

            </div>

            {/* Right Column: Sticky Sidebar with Pricing Card & Quick Info (5 cols on desktop) */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-28">
              
              {/* Pricing Card */}
              <div className="border border-neutral-900 rounded-2xl p-6 sm:p-7 bg-brand-50/40 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 rounded-full blur-2xl pointer-events-none" />
                
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-display font-medium text-neutral-900">
                    Priser for clairvoyance
                  </h3>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-100 text-brand-800 border border-brand-200">
                    Samme pris
                  </span>
                </div>

                <p className="text-xs text-neutral-600 mb-5 leading-relaxed">
                  Priserne gælder både clairvoyance ved fremmøde i Viby J og rådgivning online eller over telefonen.
                </p>

                <ul className="space-y-3.5 text-neutral-800">
                  <li className="flex justify-between items-center pb-3 border-b border-neutral-200/80">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-brand-600" />
                      <span className="font-medium text-neutral-700">15 min. clairvoyance</span>
                    </div>
                    <span className="font-bold text-brand-700 text-xl font-display">350 kr.</span>
                  </li>
                  <li className="flex justify-between items-center pb-3 border-b border-neutral-200/80">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-brand-600" />
                      <span className="font-medium text-neutral-700">30 min. clairvoyance</span>
                    </div>
                    <span className="font-bold text-brand-700 text-xl font-display">650 kr.</span>
                  </li>
                  <li className="flex justify-between items-center pb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-brand-600" />
                      <span className="font-medium text-neutral-700">60 min. clairvoyance</span>
                    </div>
                    <span className="font-bold text-brand-700 text-xl font-display">1.200 kr.</span>
                  </li>
                </ul>

                <p className="text-xs text-neutral-500 mt-4 pt-3 border-t border-neutral-200/60 leading-relaxed">
                  Er du i tvivl om tiden, kan vi afklare det sammen før bestilling, så rammen passer til dit behov. Du kan også købe en session direkte i min <Link to="/butik" className="text-brand-700 hover:text-brand-800 underline font-medium">online butik</Link>.
                </p>

                <div className="mt-5">
                  <a
                    href="#kontakt"
                    className="w-full py-3 px-4 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-xl text-center flex items-center justify-center gap-2 text-sm transition-colors shadow-sm"
                  >
                    <Calendar className="w-4 h-4" />
                    Bestil tid til clairvoyance
                  </a>
                </div>
              </div>

              {/* Location & Practice Info Card */}
              <div className="bg-neutral-50 border border-neutral-200/80 rounded-2xl p-6 space-y-4">
                <div className="flex items-center gap-2 text-neutral-900 font-display font-medium text-lg">
                  <MapPin className="w-5 h-5 text-brand-600" />
                  <span>Praksis & Konsultation</span>
                </div>
                <div className="space-y-2 text-sm text-neutral-600 leading-relaxed">
                  <p className="font-medium text-neutral-800">
                    Lykkesholms Allé 102, 1. th.<br />
                    8260 Viby J (Aarhus)
                  </p>
                  <p className="text-xs text-neutral-500">
                    Gratis parkering tæt ved døren. Letbane og busforbindelser lige i nærheden.
                  </p>
                </div>
                <div className="pt-2 border-t border-neutral-200/70">
                  <a
                    href="tel:40938593"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    +45 40 93 85 93
                  </a>
                </div>
              </div>

              {/* Recording Tip Card */}
              <div className="p-5 rounded-2xl bg-brand-50/50 border border-brand-200/60 text-sm text-neutral-700 flex items-start gap-3.5">
                <div className="p-2 rounded-lg bg-white text-brand-600 shadow-sm shrink-0 mt-0.5">
                  <Mic className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900 mb-1">
                    Du må gerne optage din clairvoyance
                  </p>
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    Under en clairvoyance kan der komme mange vigtige pointer og indtryk frem. Du er altid meget velkommen til at optage samtalen på din smartphone, så du kan lytte til din clairvoyance i ro og mag derhjemme.
                  </p>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title="Ofte stillede spørgsmål om clairvoyance"
        description="Få svar på de mest almindelige spørgsmål om clairvoyance, forberedelse, etik og hvordan en session foregår i praksis."
        badge="Spørgsmål & Svar"
        items={[
          {
            question: "Hvordan forbereder jeg mig bedst til en clairvoyance-session?",
            answer:
              "Du behøver ikke at forberede noget særligt forud for en session hos mig. Det er dog en stor fordel, hvis du på forhånd har overvejet, hvilke livsområder eller konkrete spørgsmål der fylder mest, og eventuelt har noteret dem ned. Mød gerne op med et åbent sind og i god ro, så du bedst muligt kan tage imod de indsigter og budskaber, der kommer frem undervejs."
          },
          {
            question: "Virker clairvoyance lige så godt online eller over telefonen som ved fysisk møde?",
            answer:
              "Ja, clairvoyant rådgivning fungerer præcis lige så præcist og dybtgående over telefonen eller via online videoforbindelse som ved personligt fremmøde. Energier og intuitive indtryk er ikke begrænset af fysisk afstand eller geografi. Du modtager den samme nærværende vejledning og ro, uanset om du sidder hjemme i din egen stue eller møder mig i klinikken i Viby J ved Aarhus."
          },
          {
            question: "Må jeg optage samtalen undervejs, så jeg kan lytte til den derhjemme?",
            answer:
              "Ja, du er meget velkommen til at optage hele sessionen på din smartphone eller anden optager. Under samtalen berører vi mange detaljer, følelser og mulige perspektiver, som kan være svære at rumme og huske fuldt ud i øjeblikket. Når du lytter til optagelsen efterfølgende i vante og rolige rammer, vil nye sammenhænge og vigtige pointer ofte træde endnu tydeligere frem for dig."
          },
          {
            question: "Kan jeg få kontakt til en specifik afdød eller få sikre svar om min fremtid?",
            answer:
              "Inden for seriøs og etisk clairvoyance gives der aldrig garantier for kontakt til en bestemt afdød eller forudsigelser med facitliste. Fremtiden er altid levende og formes løbende af dine egne frie valg, handlinger og udvikling. Rådgivningen skal ses som et kærligt og spirituelt kompas, der belyser dine muligheder og blinde vinkler, men ansvaret for dit liv forbliver altid dit eget."
          },
          {
            question: "Hvor lang tid bør jeg vælge til min første clairvoyance-session?",
            answer:
              "Til en førstegangs clairvoyance anbefaler jeg som oftest en session på 30 eller 60 minutter. 30 minutter er velegnet til at komme i dybden med et par centrale temaer, mens 60 minutter giver maksimal ro og god tid til fordybelse og opfølgende spørgsmål. 15 minutter er primært henvendt til en hurtig, fokuseret afklaring af et enkelt, afgrænset emne i hverdagen."
          }
        ]}
      />

      {/* Contact Section */}
      <ContactSection 
        title="Bestil tid til Clairvoyance" 
        description="Udfyld formularen her med dine oplysninger og ønsker til sessionen (online, telefon eller fysisk fremmøde i Viby J). Jeg kontakter dig hurtigst muligt for at aftale det præcise tidspunkt." 
      />
    </>
  );
}
